<template>
  <section ref="section" class="aperture-section" :style="apertureStyle">
    <div class="aperture-frame">
      <video
        v-if="video"
        ref="videoElement"
        class="aperture-video"
        autoplay
        muted
        loop
        playsinline
        preload="metadata"
        :src="video"
        :poster="image"
        :aria-label="alt"
        @canplay="startVideo"
      ></video>
      <div v-else class="aperture-photo" role="img" :aria-label="alt"></div>
      <div class="aperture-shade"></div>
      <div class="aperture-ring aperture-ring-a"></div>
      <div class="aperture-ring aperture-ring-b"></div>
      <div class="aperture-copy">
        <p class="eyebrow">The work behind the standard</p>
        <h2>Built for the field.</h2>
        <p>Real service, real equipment, and a record of care designed to protect what your machines are worth.</p>
      </div>
    </div>
  </section>
</template>

<script>
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

export default {
  props: {
    image: {
      type: String,
      default: '',
    },
    video: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: 'Centerfire field service work',
    },
  },
  data() {
    return {
      frame: 0,
      geometry: {
        ax: -10,
        ay: -12,
        bx: 110,
        by: 112,
        radius: 1,
        reveal: 0,
      },
    };
  },
  computed: {
    apertureStyle() {
      return {
        '--aperture-image': `url('${this.image}')`,
        '--circle-a-x': `${this.geometry.ax}%`,
        '--circle-a-y': `${this.geometry.ay}%`,
        '--circle-b-x': `${this.geometry.bx}%`,
        '--circle-b-y': `${this.geometry.by}%`,
        '--circle-radius': `${this.geometry.radius}px`,
        '--copy-opacity': this.geometry.reveal,
      };
    },
  },
  mounted() {
    this.updateAperture();
    this.$nextTick(this.startVideo);
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.requestUpdate, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.requestUpdate);
    window.cancelAnimationFrame(this.frame);
  },
  methods: {
    startVideo() {
      this.$refs.videoElement?.play().catch(() => {});
    },
    requestUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.updateAperture();
      });
    },
    updateAperture() {
      const section = this.$refs.section;
      if (!section) return;

      const frame = section.querySelector('.aperture-frame');
      const rect = section.getBoundingClientRect();
      const rawProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const progress = clamp(rawProgress);
      const width = frame?.clientWidth || window.innerWidth || 1;
      const height = frame?.clientHeight || window.innerHeight || 1;

      // Two opposing diagonal paths cross near the section midpoint. At the
      // crossing, the circles are deliberately larger than the frame's short
      // side, revealing nearly the entire photograph while leaving its corners
      // outside the shared lens.
      this.geometry = {
        ax: -12 + progress * 124,
        ay: -18 + progress * 136,
        bx: 112 - progress * 124,
        by: 118 - progress * 136,
        radius: Math.max(width * 0.58, height * 0.79),
        reveal: clamp(1 - Math.abs(progress - 0.5) * 5.2),
      };
    },
  },
};
</script>

<style scoped>
.aperture-section {
  position: relative;
  width: min(94vw, 1500px);
  margin: clamp(2rem, 5vw, 5rem) auto;
  padding: 0;
}

.aperture-frame {
  position: relative;
  height: clamp(520px, 82vh, 860px);
  overflow: hidden;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.018);
  border: 1px solid rgba(255, 255, 255, 0.08);
  isolation: isolate;
}

.aperture-photo,
.aperture-video,
.aperture-shade {
  position: absolute;
  inset: 0;
  -webkit-mask-image:
    radial-gradient(circle var(--circle-radius) at var(--circle-a-x) var(--circle-a-y), #000 0 99%, transparent 100%),
    radial-gradient(circle var(--circle-radius) at var(--circle-b-x) var(--circle-b-y), #000 0 99%, transparent 100%);
  -webkit-mask-composite: source-in;
  mask-image:
    radial-gradient(circle var(--circle-radius) at var(--circle-a-x) var(--circle-a-y), #000 0 99%, transparent 100%),
    radial-gradient(circle var(--circle-radius) at var(--circle-b-x) var(--circle-b-y), #000 0 99%, transparent 100%);
  mask-composite: intersect;
}

.aperture-photo {
  z-index: 0;
  background-image: var(--aperture-image);
  background-size: cover;
  background-position: center;
  transform: scale(1.035);
  filter: saturate(0.82) contrast(1.04);
}

.aperture-video {
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1.035);
  filter: saturate(0.82) contrast(1.04);
}

.aperture-shade {
  z-index: 1;
  background: linear-gradient(180deg, transparent 36%, rgba(0, 0, 0, 0.78) 100%);
}

.aperture-ring {
  position: absolute;
  z-index: 2;
  width: calc(var(--circle-radius) * 2);
  height: calc(var(--circle-radius) * 2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  will-change: left, top;
}

.aperture-ring-a {
  left: var(--circle-a-x);
  top: var(--circle-a-y);
  border: 1px solid rgba(255, 90, 2, 0.5);
  box-shadow: 0 0 44px rgba(255, 90, 2, 0.16);
}

.aperture-ring-b {
  left: var(--circle-b-x);
  top: var(--circle-b-y);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.07);
}

.aperture-copy {
  position: absolute;
  z-index: 3;
  left: clamp(1.5rem, 5vw, 5rem);
  right: clamp(1.5rem, 5vw, 5rem);
  bottom: clamp(1.7rem, 6vw, 5rem);
  display: grid;
  gap: 0.8rem;
  max-width: 690px;
  opacity: var(--copy-opacity);
  transform: translateY(calc((1 - var(--copy-opacity)) * 20px));
  transition: opacity 0.16s linear;
}

.aperture-copy h2 {
  width: max-content;
  max-width: 100%;
  font-size: clamp(1.8rem, 6vw, 6rem);
  font-weight: 200;
  line-height: 0.95;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.aperture-copy > p:last-child {
  max-width: 54ch;
  color: var(--white);
  font-size: clamp(1rem, 1.7vw, 1.2rem);
}

@media (max-width: 760px) {
  .aperture-section {
    width: 94vw;
  }

  .aperture-frame {
    height: min(74vh, 680px);
    min-height: 500px;
    border-radius: 22px;
  }

  .aperture-photo,
  .aperture-video {
    background-position: 38% center;
    object-position: 38% center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aperture-copy {
    transition: none;
  }
}
</style>
